import React, { useEffect, useState } from "react"
import {
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material"
import { useParams } from "react-router-dom"
import api from "../../../utils/api"

interface EventSummary {
  id: string
  name: string
  description: string
  adminLimit: number
  numVoters: number
  endDate: Date
  startDate: Date
  archiveStatus: boolean
}

interface VotingInstance {
  id: string
  description: string
  eventId: any
  questionCount: number
  status: string
  questionNum: number
  createdAt: Date
}

interface Voter {
  id: string
  name: string
  email: string
  username: string
  voted: boolean
}

const convertToDate = (dateString: string): Date => {
  return new Date(dateString)
}

export const ArchivedInformationPage: React.FC = () => {
  const [event, setEvent] = useState<EventSummary | null>(null)
  const [votingInstances, setVotingInstances] = useState<VotingInstance[]>([])
  const [voters, setVoters] = useState<Voter[]>([])
  const { id } = useParams<{ id: string }>()

  useEffect(() => {
    if (id) {
      api
        .get(`/admin/events/${id}`)
        .then((response) => {
          setEvent({
            ...response.data,
            startDate: convertToDate(response.data.startDate),
            endDate: convertToDate(response.data.endDate),
          })
        })
        .catch((error) => {
          console.error("Error fetching event details:", error)
        })
    } else {
      console.error("Event ID is undefined")
    }
  }, [id])

  useEffect(() => {
    if (id) {
      api
        .get(`/admin/events/${id}/voters`)
        .then((response) => setVoters(response.data))
        .catch((error) => console.error("Error fetching voters:", error))
    }
  }, [id])

  useEffect(() => {
    if (id) {
      api
        .get(`/admin/events/${id}/voting-instances`)
        .then((response) => setVotingInstances(response.data))
        .catch((error) =>
          console.error("Error fetching voting instances:", error)
        )
    }
  }, [id])

  if (!event) {
    return (
      <Typography variant="h6" m={4}>
        Loading event data...
      </Typography>
    )
  }

  const handleViewResults = (instanceId: string): void => {
    window.location.href = `/admin/event-admin/instance/${instanceId}/results`
  }

  return (
    <>
      <Typography variant="h6" m={4}>
        {event.name} - Archived Information
      </Typography>
      <Paper elevation={3}>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>
                <Typography variant="subtitle1" component="span">
                  Event Name:
                </Typography>
              </TableCell>
              <TableCell>{event.name}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography variant="subtitle1" component="span">
                  Start Date:
                </Typography>
              </TableCell>
              <TableCell>
                {event.startDate
                  ? event.startDate.toLocaleDateString()
                  : "Loading..."}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography variant="subtitle1" component="span">
                  End Date:
                </Typography>
              </TableCell>
              <TableCell>
                {event.endDate
                  ? event.endDate.toLocaleDateString()
                  : "Loading..."}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography variant="subtitle1" component="span">
                  Event ID:
                </Typography>
              </TableCell>
              <TableCell>{id}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography variant="subtitle1" component="span">
                  Description:
                </Typography>
              </TableCell>
              <TableCell>{event.description}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography variant="subtitle1" component="span">
                  Number of Voters:
                </Typography>
              </TableCell>
              <TableCell>{event.numVoters}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>

      <Paper elevation={3} sx={{ mt: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="subtitle1" component="span">
                  Instance
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle1" component="span">
                  Question Count
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle1" component="span">
                  Status
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle1" component="span">
                  Results
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {votingInstances.map((instance) => (
              <TableRow key={instance.eventId}>
                <TableCell>{instance.description}</TableCell>
                <TableCell>{instance.questionCount}</TableCell>
                <TableCell>{instance.status}</TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleViewResults(instance.id)}
                  >
                    View Results
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="h6" m={4}>
        Voters
      </Typography>
      <Paper elevation={3} sx={{ mt: 4 }}>
        {voters.length > 0 ? (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Username</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {voters.map((voter) => (
                <TableRow key={voter.id}>
                  <TableCell>{voter.name}</TableCell>
                  <TableCell>{voter.email}</TableCell>
                  <TableCell>{voter.username}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="h6" m={2}>
            No voters for this event.
          </Typography>
        )}
      </Paper>
    </>
  )
}

export default ArchivedInformationPage
